import { useQuery } from '@tanstack/react-query'
import { vocabularyApi } from '@/api/vocabulary'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'

interface ReviewHistoryTimelineProps {
  userWordId: string
}

const RATING_COLORS: Record<number, string> = {
  0: 'bg-destructive',
  1: 'bg-orange-500',
  2: 'bg-green-600',
  3: 'bg-emerald-500',
}

export function ReviewHistoryTimeline({ userWordId }: ReviewHistoryTimelineProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['vocabulary', userWordId, 'review-history'],
    queryFn: () => vocabularyApi.getReviewHistory(userWordId).then((r) => r.data),
  })

  if (isLoading) {
    return (
      <div className="mb-6 flex justify-center py-4">
        <LoadingSpinner />
      </div>
    )
  }

  if (!data?.items.length) {
    return null
  }

  return (
    <div className="mb-6 rounded-xl border bg-card p-4" data-testid="review-history-timeline">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {copy.word.reviewHistory}
      </h3>
      <ol className="space-y-2">
        {data.items.map((item, i) => (
          <li
            key={`${item.reviewedAt}-${i}`}
            className="flex items-center gap-3 text-sm"
            data-testid="review-history-item"
          >
            <span
              className={cn(
                'inline-flex h-6 min-w-[3.5rem] items-center justify-center rounded-full px-2 text-xs font-medium text-white',
                RATING_COLORS[item.rating] ?? 'bg-muted-foreground',
              )}
            >
              {copy.word.reviewRating[item.rating] ?? `R${item.rating}`}
            </span>
            <span className="text-muted-foreground">
              {new Date(item.reviewedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {item.intervalDaysAfter}d
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}
